import { fetchFaqHubProductPage } from "@/api/faqHubApi";
import FaqHubFaqPanel from "@/components/FaqHub/FaqHubFaqPanel";
import FaqHubProductSummary from "@/components/FaqHub/FaqHubProductSummary";
import FaqHubRelatedProducts from "@/components/FaqHub/FaqHubRelatedProducts";
import ContactUsForm from "@/components/us-website/ContactUsForm/ContactUsForm";
import MetaTags from "@/components/seo/meta-tags";
import { BreadcrumbJsonLd, FAQPageJsonLd } from "next-seo";
import { slugify } from "@/utils/Common";
import { faqAnswerToPlainText } from "@/utils/faqHubAnswerHtml";
import { FAQ_SLUG } from "@/constastants/Constant";

export default function FaqHubProductPage({ data }) {
  const { faqs, related_products, faq_meta, ...product } = data;

  const defaultTitle = `FAQs: ${product?.ChemicalName || "Product"} | Elchemy`;
  const defaultDescription =
    product?.summary?.length > 0
      ? String(product.summary).slice(0, 158).trim()
      : `Common questions and answers about ${product?.ChemicalName} — ordering, specifications, and more from Elchemy.`;

  const title = faq_meta?.page_title ?? defaultTitle;
  const metaTitle = faq_meta?.meta_title ?? title;
  const description = faq_meta?.meta_description ?? defaultDescription;
  const sectionTitle = faq_meta?.faq_section_title ?? "Product FAQs";

  const slug = `${slugify(product?.ChemicalName || "")}-${product?.id}`;
  const canonicalPath = `/${FAQ_SLUG}/${slug}`;

  const faqJsonLd =
    Array.isArray(faqs) && faqs.length
      ? faqs.map((f) => ({
          questionName: f.question,
          acceptedAnswerText: faqAnswerToPlainText(f.answer),
        }))
      : [];

  return (
    <>
      <MetaTags
        title={title}
        metaTitle={metaTitle}
        description={description}
        defaultCanonical={canonicalPath}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: "Home",
            item: `${process.env.NEXT_PUBLIC_APP_URL}/`,
          },
          {
            position: 2,
            name: "FAQs",
            item: `${process.env.NEXT_PUBLIC_APP_URL}/${FAQ_SLUG}`,
          },
          {
            position: 3,
            name: product?.ChemicalName,
            item: `${process.env.NEXT_PUBLIC_APP_URL}${canonicalPath}`,
          },
        ]}
      />
      {faqJsonLd.length > 0 ? <FAQPageJsonLd mainEntity={faqJsonLd} /> : null}

      <div className="min-h-screen bg-[#f4f4f4]">
        <div className="px-4 sm:px-6 lg:px-14 pt-8">
          <div className="max-w-[1200px] mx-auto">
            <FaqHubProductSummary product={product} pageTitle={faq_meta?.page_title} />
          </div>
        </div>

        <div className="px-4 sm:px-6 lg:px-14 mt-8 pb-16">
          <div className="max-w-[1200px] mx-auto rounded-[14px] border border-[#ececec] bg-white px-5 py-6 md:px-8 md:py-8 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
            <FaqHubFaqPanel faqs={faqs} sectionTitle={sectionTitle} />
          </div>
        </div>

        {/* <div className="px-4 sm:px-6 lg:px-14 mt-10">
          <div className="max-w-[1200px] mx-auto">
            <FaqHubRelatedProducts products={related_products} />
          </div>
        </div> */}
      </div>
      <ContactUsForm headingPrefix="Let's" headingHighlight="simplify" />
    </>
  );
}

export async function getServerSideProps(context) {
  const raw = context.params?.faqProduct || "";
  const lastDash = raw.lastIndexOf("-");
  if (lastDash < 0) {
    return { notFound: true };
  }
  const id = Number(raw.slice(lastDash + 1));
  if (!Number.isFinite(id) || id < 1) {
    return { notFound: true };
  }

  const res = await fetchFaqHubProductPage(id);
  if (!res.ok) {
    return { notFound: true };
  }

  return {
    props: {
      data: res.data,
    },
  };
}
